import { Autocomplete, Box, Checkbox, Chip, FormControlLabel, FormHelperText, Stack, TextField, Typography } from '@mui/material';

interface ClientOption {
  uid: string;
  name: string;
}

interface ClientMultiSelectProps {
  clientOptions: ClientOption[];
  // Selected client UIDs.
  value: string[];
  onChange: (uids: string[]) => void;
  allClients: boolean;
  onAllClientsChange: (allClients: boolean) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

/**
 * Audience picker for an announcement.
 *
 * `components/ClientFilter` is not reusable here — it is single-select and hides itself
 * when fewer than two options exist, which would leave a single-client deployment with
 * no way to pick an audience at all.
 *
 * "All clients" is a broadcast, not a select-all: it stores a flag rather than the
 * current client list, so clients onboarded later still see the announcement. Because
 * it supersedes any explicit selection the server clears `clients` when it is set, so
 * the list is disabled (not cleared) while the toggle is on — flipping it back off
 * restores whatever was picked before.
 */
const ClientMultiSelect = ({
  clientOptions,
  value,
  onChange,
  allClients,
  onAllClientsChange,
  disabled = false,
  error = false,
  helperText
}: ClientMultiSelectProps) => {
  const selected = clientOptions.filter((option) => value.includes(option.uid));

  return (
    <Stack spacing={1}>
      <Autocomplete
        multiple
        disableCloseOnSelect
        options={clientOptions}
        value={selected}
        disabled={disabled || allClients}
        getOptionLabel={(option) => option.name ?? ''}
        isOptionEqualToValue={(option, selectedOption) => option.uid === selectedOption.uid}
        onChange={(_event, newValue) => onChange(newValue.map((option) => option.uid))}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => <Chip {...getTagProps({ index })} key={option.uid} label={option.name} size="small" />)
        }
        renderInput={(params) => (
          <TextField {...params} label="Clients" placeholder={selected.length ? '' : 'Select clients'} error={error} />
        )}
      />

      <Box>
        <FormControlLabel
          control={<Checkbox checked={allClients} disabled={disabled} onChange={(event) => onAllClientsChange(event.target.checked)} />}
          label={
            <Stack>
              <Typography variant="body2">All clients</Typography>
              <Typography variant="caption" color="text.secondary">
                Includes clients onboarded after this is posted
              </Typography>
            </Stack>
          }
        />
      </Box>

      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </Stack>
  );
};

export default ClientMultiSelect;
